import { TeamType, UserType } from '@api';
import {
  MultiSelectField,
  TextareaField,
  TextField,
} from '@hypeit-gmbh/hypeit-ui';
import { Fieldset, Grid, Stack } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormActions } from '../FormActions';
import { PrioritySelect } from './components';
import { TaskFormSchemaType, useTaskForm } from './hooks';

const TRANS_KEY = 'FORM';

type TaskFormPropsType = {
  onSubmit: (data: TaskFormSchemaType) => Promise<void>;
  onCancel: () => void;
  defaultValues: TaskFormSchemaType;
  users: UserType[];
  teams: TeamType[];
};

export const TaskForm: React.FC<TaskFormPropsType> = ({
  onSubmit,
  onCancel,
  defaultValues,
  users,
  teams,
}) => {
  const { t } = useTranslation('tasks');
  const form = useTaskForm(defaultValues);

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.firstname} ${user.lastname}`,
  }));

  const teamOptions = teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="xs">
          <TextField
            fieldName="title"
            label={t(`${TRANS_KEY}.FIELDS.TITLE`)}
            required
          />
          <TextareaField
            fieldName="description"
            label={t(`${TRANS_KEY}.FIELDS.DESCRIPTION`)}
            required
          />
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <PrioritySelect label={t(`${TRANS_KEY}.FIELDS.PRIORITY`)} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextField
                fieldName="context"
                label={t(`${TRANS_KEY}.FIELDS.CONTEXT`)}
              />
            </Grid.Col>
          </Grid>
          <Fieldset legend={t(`${TRANS_KEY}.AUDIENCE.LABEL`)}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <MultiSelectField
                  fieldName="userIds"
                  data={userOptions}
                  label={t(`${TRANS_KEY}.FIELDS.USERS`)}
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <MultiSelectField
                  fieldName="teamIds"
                  data={teamOptions}
                  label={t(`${TRANS_KEY}.FIELDS.TEAMS`)}
                  searchable
                />
              </Grid.Col>
            </Grid>
          </Fieldset>
          <FormActions
            onCancel={onCancel}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
